<template>
  <!--
    Public preview catalogue rendered at /preview-product.

    Anonymous visitors (no token, no shift) can browse the menu. The
    layout mirrors service/TabMenu: a sticky pill bar that scroll-spies
    the page, sections grouped by category, and a synthetic
    "Recommended" bucket at the top driven by `is_recommend`.

    The page is intentionally read-only — no cart, no add-to-cart, no
    per-branch fields (stock / branch price override). Tapping a card
    opens a detail sheet with attributes / variants / modifiers
    exposed for browsing only.
  -->
  <div class="preview-shell d-flex flex-column bg-light" :class="{ 'is-scrolled': headerCollapsed }" style="min-height: 100vh;">

    <!-- Brand header -->
    <header class="align-content-center bg-theme position-sticky preview-header px-3 py-1 shadow-sm text-white top-0 z-3">
      <div class="d-flex align-items-center justify-content-between gap-2">
        <div class="d-flex align-items-center gap-2" style="min-width: 0;">
          <i class="fa-solid fa-book-open fs-5 flex-shrink-0" />
          <div class="lh-sm" style="min-width: 0;">
            <div class="fw-bold text-truncate">{{ $t('Menu Preview') }}</div>
            <div class="header-sub opacity-75 text-truncate" style="font-size: 0.7rem;">
              <i class="fa-solid fa-eye me-1" />{{ $t('Browse our menu. Ordering is not available on this page.') }}
            </div>
          </div>
        </div>

        <div class="dropdown flex-shrink-0">
          <button class="btn btn-sm btn-light rounded-pill px-3 fw-bold" data-bs-toggle="dropdown">
            <i class="fa-solid fa-globe me-1" />
            {{ currentLocale === 'th' ? 'ไทย' : 'EN' }}
          </button>
          <ul class="dropdown-menu dropdown-menu-end shadow-sm">
            <li>
              <button class="dropdown-item" :class="{ active: currentLocale === 'th' }" @click="switchLang('th')">
                🇹🇭 ไทย
              </button>
            </li>
            <li>
              <button class="dropdown-item" :class="{ active: currentLocale === 'en' }" @click="switchLang('en')">
                🇬🇧 English
              </button>
            </li>
          </ul>
        </div>
      </div>
    </header>

    <!--
      Sticky pill bar + search/sort row. Combined into one sticky region
      so the bar's bottom edge is a stable reference for the scroll-spy
      trigger line (mirrors the approach in service/TabMenu).
    -->
    <div ref="stickyBar" class="sticky-bar bg-light">
      <!-- <div class="px-3 pt-3">
        <div class="d-flex gap-2 align-items-center">
          <div class="search-pill input-group flex-grow-1">
            <span class="input-group-text bg-white border-end-0">
              <i class="fa-solid fa-magnifying-glass text-muted" />
            </span>
            <input
              v-model="searchInput"
              type="search"
              class="form-control border-start-0"
              :placeholder="$t('Search products')"
              maxlength="100"
              @keyup.enter="applyFilters"
            >
            <button
              v-if="searchInput"
              class="btn btn-outline-secondary border-start-0"
              type="button"
              :title="$t('Clear')"
              @click="clearSearch"
            >
              <i class="fa-solid fa-xmark" />
            </button>
          </div>

          <div class="dropdown flex-shrink-0">
            <button
              class="btn btn-light border rounded-pill px-3 fw-bold"
              type="button"
              data-bs-toggle="dropdown"
              :title="$t(activeSortOption.label)"
            >
              <i class="fa-solid fa-arrow-down-wide-short" />
              <span class="d-none d-md-inline ms-1">{{ $t(activeSortOption.label) }}</span>
            </button>
            <ul class="dropdown-menu dropdown-menu-end shadow-sm">
              <li v-for="opt in sortOptions" :key="opt.value">
                <button
                  class="dropdown-item"
                  :class="{ active: sort === opt.value }"
                  @click="setSort(opt.value)"
                >
                  <i :class="opt.icon" class="me-2 text-muted" />{{ $t(opt.label) }}
                </button>
              </li>
            </ul>
          </div>
        </div>
      </div> -->

      <div ref="pillBar" class="pill-bar px-3 pt-2 pb-2">
        <div v-if="loading" class="d-flex overflow-x-auto hide-scrollbar gap-2">
          <span
            v-for="i in 5"
            :key="'pill-ph-' + i"
            class="placeholder-glow flex-shrink-0"
          >
            <span class="placeholder rounded-pill" style="width: 78px; height: 30px;" />
          </span>
        </div>
        <div v-else class="d-flex overflow-x-auto hide-scrollbar gap-2">
          <button
            v-for="cat in visibleCategories"
            :key="cat.id"
            :ref="el => setPillRef(cat.id, el)"
            type="button"
            class="cat-pill flex-shrink-0"
            :class="{ active: activeCategoryId === cat.id }"
            @click="scrollToCategory(cat.id, $event)"
          >
            <i v-if="cat.id === RECOMMENDED_ID" class="fa-solid fa-star text-warning" />
            <span>{{ cat.id === RECOMMENDED_ID ? $t('Recommended') : $ln(cat) }}</span>
            <span class="cat-pill-count">{{ groupedProducts[cat.name]?.length || 0 }}</span>
          </button>
        </div>
      </div>
    </div>

    <main class="flex-grow-1 pb-5">
      <transition name="fade-swap" mode="out-in">
        <!-- Loading skeleton — same poster aspect as the real cards
             so the layout doesn't shift when the data lands. -->
        <div v-if="loading" key="sk" class="px-3 pt-3">
          <div class="row g-2">
            <div
              v-for="i in 8" :key="'sk-' + i"
              class="col-6 col-md-4 col-lg-3"
            >
              <div class="skeleton-poster skeleton-shimmer" />
            </div>
          </div>
        </div>

        <!-- Error -->
        <div v-else-if="error" key="err" class="px-3 pt-5 text-center text-muted">
          <i class="fa-solid fa-circle-exclamation fa-3x text-danger mb-3 opacity-75" />
          <p class="fw-bold mb-1">{{ $t('Failed to load menu') }}</p>
          <p class="small mb-3">{{ error }}</p>
          <button class="btn btn-theme text-white rounded-pill px-4" @click="fetchData">
            <i class="fa-solid fa-rotate me-2" />{{ $t('Try again') }}
          </button>
        </div>

        <!-- Empty -->
        <div v-else-if="!products.length" key="empty" class="px-3 pt-5 text-center text-muted">
          <i class="fa-solid fa-bowl-rice fa-3x mb-3 opacity-50" />
          <p class="fw-bold mb-0">{{ $t('No products found') }}</p>
        </div>

        <!-- Category sections -->
        <div v-else key="real" class="px-3 pt-3">
          <template v-for="(cat, ci) in visibleCategories" :key="cat.id">
            <section
              :ref="el => setSectionRef(cat.id, el)"
              :data-cat-id="cat.id"
              class="menu-section mb-4 section-enter"
              :style="{ '--enter-delay': (ci * 25) + 'ms' }"
            >
              <div class="section-heading mb-2 d-flex align-items-center">
                <span class="section-accent me-2" />
                <i v-if="cat.id === RECOMMENDED_ID" class="fa-solid fa-star me-2 text-warning" />
                <h6 class="section-title fw-bold text-dark mb-0">
                  {{ cat.id === RECOMMENDED_ID ? $t('Recommended') : $ln(cat) }}
                </h6>
                <span class="section-count ms-2">{{ groupedProducts[cat.name].length }}</span>
              </div>

              <div class="row g-2">
                <div
                  v-for="product in groupedProducts[cat.name]"
                  :key="cat.id + '-' + product.id"
                  class="col-6 col-md-4 col-lg-3"
                >
                  <div
                    class="product-card position-relative shadow-sm h-100"
                    :class="{ 'is-soldout': product.is_in_stock === false }"
                    :ref="el => setCardRef(product.id, el)"
                    :data-product-id="product.id"
                    @click="openProduct(product)"
                  >
                    <!-- @click="openProduct(product)" -->
                    <!-- Full-bleed media: image fills the card; for items
                         without an image we fall back to a theme gradient
                         so the card never looks "empty" in the grid. -->
                    <img
                      v-if="product.image_url"
                      :src="product.image_url"
                      class="product-image"
                      @error="$storeIndex.onImageError"
                    >
                    <div v-else class="product-placeholder">
                      <i class="fa-solid fa-utensils" />
                    </div>

                    <!-- Bottom-fade gradient to keep the title legible
                         over any image. Sits between the image and the
                         text/badge overlays. -->
                    <div class="product-gradient" />

                    <!-- Out-of-stock overlay (branch-scoped page only): the
                         list carries is_in_stock === false when the branch
                         has the product on-sale but out of stock. -->
                    <div v-if="product.is_in_stock === false" class="soldout-overlay">
                      <span class="soldout-badge">
                        <i class="fa-solid fa-ban me-1" />{{ $t('Out of Stock') }}
                      </span>
                    </div>

                    <!--
                      Top-left badge stack (mirrors service/TabMenu):
                      • Price chip — prepends a star icon only when this
                        card lives in the synthetic Recommended bucket so
                        the same product in its real category section
                        doesn't double-up on the star treatment.
                      • Options chip — flags products that have variants
                        or modifiers worth opening the sheet for.
                    -->
                    <div class="overlay-badges">
                      <span v-if="priceLabel(product)" class="overlay-price">
                        <i
                          v-if="product.is_recommend && cat.id === RECOMMENDED_ID"
                          class="fa-solid fa-star text-warning me-1"
                        />
                        {{ priceLabel(product) }}
                        <span v-if="comparePriceLabel(product)" class="text-decoration-line-through fw-normal ms-1" style="font-size: 0.72em; opacity: 0.7;">
                          {{ comparePriceLabel(product) }}
                        </span>
                      </span>
                      <span
                        v-if="product.has_variants || product.has_modifiers"
                        class="overlay-options"
                      >
                        <i class="fa-solid fa-sliders me-1" />{{ $t('Options') }}
                      </span>
                    </div>

                    <!-- Bottom text block: just the menu name. Category +
                         price are intentionally omitted here — the section
                         header above the card carries the category, and
                         the top-left chip carries the price. -->
                    <div class="product-overlay text-white">
                      <h6 class="overlay-name fw-bold mb-0">
                        {{ $ln(product) }}
                      </h6>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </template>
        </div>
      </transition>
    </main>

    <product-detail-sheet
      v-if="activeProduct"
      :product="activeProduct"
      @close="activeProduct = null"
    />
  </div>
</template>

<script>
import { PreviewProducts } from '@/services/endpoint/preview.service';
import ProductDetailSheet from './ProductDetailSheet.vue';
import { setLocale } from '@/plugins/i18n';

// Sentinels for the synthetic "Recommended" bucket — mirrors TabMenu so
// the same scroll-spy logic works without special-casing nullable ids.
const RECOMMENDED_ID = '__recommended';
const RECOMMENDED_NAME = '__recommended_section__';

export default {
  name: 'PreviewProduct',
  components: { ProductDetailSheet },
  data() {
    return {
      RECOMMENDED_ID,

      // --- API state ----------------------------------------------------
      loading: true,
      error: '',
      products: [],
      categories: [],

      // --- Filters ------------------------------------------------------
      // `searchInput` is the live input; `search` is what gets sent to
      // the API (committed on Enter or after a 300ms idle) so each
      // keystroke doesn't trigger a round-trip + full re-render.
      searchInput: '',
      search: '',
      sort: 'name|asc',
      _searchTimer: null,

      // Sort whitelist mirrors the API (name, price, unit, type, category, created_at).
      sortOptions: [
        { value: 'price|asc',       label: 'Price (low→high)', icon: 'fa-solid fa-arrow-down-1-9' },
        { value: 'price|desc',      label: 'Price (high→low)', icon: 'fa-solid fa-arrow-up-9-1' }
      ],

      // --- UI state -----------------------------------------------------
      activeCategoryId: null,
      sectionEls: {},          // refs keyed by category id
      pillEls: {},
      _suppressSpyUntil: 0,    // ms timestamp; scroll-spy is ignored until then
      _onScroll: null,
      _onResize: null,
      _rafPending: false,

      // Collapsing header: folds the subtitle + shrinks the bar on scroll
      // so more of the catalogue is visible. Hysteresis lives in _onScroll.
      headerCollapsed: false,

      activeProduct: null,

      // --- Price range cache --------------------------------------------
      // The list API doesn't return variants, only `has_variants`. To show
      // a real "฿min – ฿max" range on each card we lazy-fetch the detail
      // when the card scrolls into view (IntersectionObserver below) and
      // cache the computed range here so the same product isn't refetched.
      // Keyed by product.id → { min, max } or `null` while in-flight / failed.
      priceRanges: {},
      cardEls: {},             // product.id → DOM el, observed by IO
      _priceObserver: null,
      _priceFetchInflight: new Set(),
    };
  },
  computed: {
    /** Branch code from /preview-product/:code — empty on the all-products route. */
    branchCode() {
      return this.$route.params.code || '';
    },
    currentLocale() {
      return this.$i18n.locale;
    },
    activeSortOption() {
      return this.sortOptions.find(o => o.value === this.sort) || this.sortOptions[0];
    },
    recommendedProducts() {
      return this.products.filter(p => p.is_recommend);
    },
    /**
     * Group products by category NAME (the list response only exposes
     * the label, no id back-ref). Display order follows the
     * filter.categories array from the API so products appearing in any
     * order still render in the configured category order. Recommended
     * items get a synthetic bucket at the top.
     */
    groupedProducts() {
      const groups = {};
      if (this.recommendedProducts.length) {
        groups[RECOMMENDED_NAME] = this.recommendedProducts;
      }
      for (const cat of this.categories) groups[cat.name] = [];
      for (const p of this.products) {
        if (groups[p.category]) {
          groups[p.category].push(p);
        } else if (p.category) {
          // Defensive: server returned a product whose category isn't in
          // filter.categories — keep it visible rather than hiding it.
          groups[p.category] = [p];
        }
      }
      return groups;
    },
    /** Hide empty categories so the pill bar isn't padded with no-op tabs. */
    visibleCategories() {
      const known = new Set(this.categories.map(c => c.name));
      const extras = Object.keys(this.groupedProducts)
        .filter(name => name !== RECOMMENDED_NAME && !known.has(name))
        .map(name => ({ id: 'x-' + name, name }));
      const real = [...this.categories, ...extras]
        .filter(c => (this.groupedProducts[c.name] || []).length > 0);
      const list = [];
      if (this.recommendedProducts.length) {
        list.push({ id: RECOMMENDED_ID, name: RECOMMENDED_NAME });
      }
      list.push(...real);
      return list;
    },
  },
  watch: {
    // 300ms idle debounce. Avoids hammering the API while typing.
    searchInput(v) {
      if (this._searchTimer) clearTimeout(this._searchTimer);
      this._searchTimer = setTimeout(() => {
        const next = v.trim();
        if (this.search !== next) {
          this.search = next;
          this.fetchData();
        }
      }, 300);
    },
    visibleCategories: {
      handler(next) {
        if (!next.length) {
          this.activeCategoryId = null;
          return;
        }
        if (!next.find(c => c.id === this.activeCategoryId)) {
          this.activeCategoryId = next[0].id;
        }
        this.$nextTick(() => this.updateActiveFromScroll());
      },
    },
    activeCategoryId(id) {
      this.scrollActivePillIntoView(id);
    },
  },
  mounted() {
    this._onScroll = () => {
      // rAF throttle: collapse a burst of scroll events into one update.
      if (this._rafPending) return;
      this._rafPending = true;
      requestAnimationFrame(() => {
        this._rafPending = false;
        this.updateActiveFromScroll();
        // Hysteresis: collapse past 56px, expand only once near the top
        // again (<16px) so a position resting on the line can't flicker.
        const y = window.scrollY;
        if (!this.headerCollapsed && y > 56) this.headerCollapsed = true;
        else if (this.headerCollapsed && y < 16) this.headerCollapsed = false;
      });
    };
    this._onResize = () => this.updateActiveFromScroll();
    window.addEventListener('scroll', this._onScroll, { passive: true });
    window.addEventListener('resize', this._onResize);

    // Lazy price-range fetch. IO is the right tool here: cards near the
    // viewport fire one detail request each, cards deep in a long list
    // don't fetch until the user actually scrolls to them.
    if (typeof IntersectionObserver !== 'undefined') {
      this._priceObserver = new IntersectionObserver((entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          const id = Number(entry.target.dataset.productId);
          if (id) this.fetchPriceRange(id);
          // One-shot: stop observing once we've kicked off the fetch.
          this._priceObserver.unobserve(entry.target);
        }
      }, { rootMargin: '200px 0px' });
    }

    this.fetchData();
  },
  beforeUnmount() {
    if (this._searchTimer) clearTimeout(this._searchTimer);
    if (this._onScroll) window.removeEventListener('scroll', this._onScroll);
    if (this._onResize) window.removeEventListener('resize', this._onResize);
    if (this._priceObserver) this._priceObserver.disconnect();
  },
  methods: {
    switchLang(lang) {
      setLocale(lang);
    },
    setSort(value) {
      if (this.sort === value) return;
      this.sort = value;
      this.fetchData();
    },
    clearSearch() {
      this.searchInput = '';
      if (this.search !== '') {
        this.search = '';
        this.fetchData();
      }
    },
    applyFilters() {
      // Enter-key path: bypass debounce so the user gets an immediate response.
      const next = this.searchInput.trim();
      if (this.search === next) return;
      this.search = next;
      this.fetchData();
    },

    /** Build the query. Skip empties — extra/blank keys 422 the API. */
    buildQuery() {
      const parts = [];
      if (this.search) parts.push('search=' + encodeURIComponent(this.search));
      if (this.sort) parts.push('sort=' + encodeURIComponent(this.sort));
      return parts.join('&');
    },

    async fetchData() {
      this.loading = true;
      this.error = '';
      try {
        // มี branch code (route /preview-product/:code) → เมนูเฉพาะสาขา; ไม่มี → เมนูรวมเดิม
        const res = this.branchCode
          ? await PreviewProducts.listByBranch(this.branchCode, this.buildQuery())
          : await PreviewProducts.list(this.buildQuery());
        const payload = res.data?.data ?? [];
        const filter = res.data?.filter ?? {};
        this.products = Array.isArray(payload) ? payload : [];
        this.categories = Array.isArray(filter.categories) ? filter.categories : [];

        // Seed the active pill on first paint so scroll-spy has a baseline.
        const first = this.visibleCategories[0];
        this.activeCategoryId = first ? first.id : null;
      } catch (err) {
        this.error = err?.response?.data?.message || err?.message || this.$t('Network error');
        this.products = [];
      } finally {
        this.loading = false;
        this.$nextTick(() => this.updateActiveFromScroll());
      }
    },

    openProduct(product) {
      this.activeProduct = product;
    },

    // --- Price range (lazy) ---------------------------------------------
    /**
     * What to render in the price chip.
     * Priority order:
     *   1. Cached variant range → "฿min – ฿max" (or single value if equal)
     *   2. Flat parent price when > 0
     *   3. Empty string → chip is hidden (avoids "฿0.00" on listing rows)
     */
    priceLabel(product) {
      const fmt = this.$storeIndex.formatThaiCurrency;
      const range = this.priceRanges[product.id];
      if (range && range.min != null) {
        if (range.min === range.max) return fmt(range.min);
        // Compact form for ranges so the chip fits inside a 2-col phone
        // card. formatThaiCurrency emits "฿55.00" which doubled up with
        // a separator overflows the badge — drop decimals for the range.
        return `฿${Math.round(range.min)}–฿${Math.round(range.max)}`;
      }
      const flat = parseFloat(product.price);
      if (!isNaN(flat) && flat > 0) return fmt(flat);
      return '';
    },

    /**
     * ราคาเต็ม (compare_at_price) สำหรับแสดงขีดฆ่าข้างราคาจริง — โชว์เฉพาะ
     * เมื่อสูงกว่าราคาขายจริง (สินค้าแบบ flat price เท่านั้น). คืนค่าว่าง = ไม่โชว์
     */
    comparePriceLabel(product) {
      const compare = Number(product.compare_at_price);
      const flat = parseFloat(product.price);
      if (!isNaN(compare) && !isNaN(flat) && flat > 0 && compare > flat) {
        return this.$storeIndex.formatThaiCurrency(compare);
      }
      return '';
    },

    /**
     * Function ref for each product card. Wires the card up to the
     * price-range IntersectionObserver if the product has variants AND
     * we haven't already fetched it (cache hit) AND a fetch isn't
     * already in flight. For variant-less products there's nothing to
     * resolve — the flat parent price is already correct.
     */
    setCardRef(id, el) {
      if (el) {
        this.cardEls[id] = el;
        const product = this.products.find(p => p.id === id);
        if (
          this._priceObserver
          && product?.has_variants
          && !(id in this.priceRanges)
          && !this._priceFetchInflight.has(id)
        ) {
          this._priceObserver.observe(el);
        }
      } else {
        if (this._priceObserver && this.cardEls[id]) {
          this._priceObserver.unobserve(this.cardEls[id]);
        }
        delete this.cardEls[id];
      }
    },

    async fetchPriceRange(productId) {
      if (productId in this.priceRanges) return;
      if (this._priceFetchInflight.has(productId)) return;
      this._priceFetchInflight.add(productId);
      try {
        const res = await PreviewProducts.info(productId);
        const raw = res.data?.data ?? res.data;
        const variants = Array.isArray(raw?.variants?.data)
          ? raw.variants.data
          : Array.isArray(raw?.variants) ? raw.variants : [];
        const prices = variants
          .map(v => parseFloat(v.price))
          .filter(n => !isNaN(n) && n > 0);
        if (prices.length) {
          this.priceRanges = {
            ...this.priceRanges,
            [productId]: { min: Math.min(...prices), max: Math.max(...prices) },
          };
        } else {
          // Mark as resolved-but-empty so we don't refetch on every scroll.
          this.priceRanges = { ...this.priceRanges, [productId]: null };
        }
      } catch (err) {
        // Silent fail: the parent price (if any) stays visible. Mark as
        // attempted so we don't retry on every IO tick.
        this.priceRanges = { ...this.priceRanges, [productId]: null };
      } finally {
        this._priceFetchInflight.delete(productId);
      }
    },

    // --- Ref helpers (Vue 3 function refs) -------------------------------
    setSectionRef(id, el) {
      if (el) this.sectionEls[id] = el;
      else delete this.sectionEls[id];
    },
    setPillRef(id, el) {
      if (el) this.pillEls[id] = el;
      else delete this.pillEls[id];
    },

    // --- Scroll-spy ------------------------------------------------------
    /**
     * The active category is the LAST section whose top has scrolled
     * past our trigger line (= bottom of the sticky bar, plus a small
     * buffer). Same approach as service/TabMenu — IntersectionObserver
     * proved fragile when sections had wildly different heights.
     */
    updateActiveFromScroll() {
      if (Date.now() < this._suppressSpyUntil) return;
      if (!this.visibleCategories.length) return;

      const trigger = this.stickyOffset() + 8;
      let activeId = this.visibleCategories[0].id;

      for (const cat of this.visibleCategories) {
        const el = this.sectionEls[cat.id];
        if (!el) continue;
        const top = el.getBoundingClientRect().top;
        if (top <= trigger) {
          activeId = cat.id;
        } else {
          // visibleCategories is in display order — once a section is
          // below the trigger, every later one is too.
          break;
        }
      }

      if (activeId !== this.activeCategoryId) {
        this.activeCategoryId = activeId;
      }
    },

    /**
     * Bottom of the sticky toolbar in viewport coords — used both as the
     * smooth-scroll offset and the scroll-spy trigger.
     */
    stickyOffset() {
      const stickyBar = this.$refs.stickyBar;
      return stickyBar ? stickyBar.getBoundingClientRect().bottom : 0;
    },

    scrollToCategory(id, ev) {
      const el = this.sectionEls[id];
      if (!el) return;
      // Release focus so a tapped pill on touch devices doesn't keep
      // its sticky :hover/:focus highlight (Bootstrap's outline-secondary
      // fills the bg on either, making non-active pills look active).
      ev?.currentTarget?.blur();

      this.activeCategoryId = id;
      // Suppress scroll-spy during the smooth scroll so intermediate
      // sections crossing the trigger don't flicker the active pill.
      this._suppressSpyUntil = Date.now() + 700;

      const top = el.getBoundingClientRect().top + window.scrollY - this.stickyOffset() - 4;
      window.scrollTo({ top, behavior: 'smooth' });
    },

    scrollActivePillIntoView(id) {
      const pill = this.pillEls[id];
      const bar = this.$refs.pillBar;
      if (!pill || !bar) return;
      const scroller = bar.querySelector('.hide-scrollbar');
      if (!scroller) return;
      const pillRect = pill.getBoundingClientRect();
      const barRect = scroller.getBoundingClientRect();
      // Centre the active pill horizontally so the user can always see
      // the neighbours on either side.
      const target = scroller.scrollLeft
        + (pillRect.left - barRect.left)
        - (barRect.width / 2)
        + (pillRect.width / 2);
      scroller.scrollTo({ left: target, behavior: 'smooth' });
    },
  },
};
</script>

<style scoped>
.preview-shell {
  font-size: 0.95rem;
  --header-h: 3rem;
}
/*
  Collapsing header: on scroll the subtitle folds away and the bar
  shrinks, freeing vertical space for the catalogue. `--header-h` drops
  too, so the sticky pill bar (anchored to it) rides up in step.
  Hysteresis lives in the scroll handler.
*/
.preview-shell.is-scrolled {
  --header-h: 2.25rem;
}
.preview-header {
  min-height: var(--header-h);
  background: linear-gradient(135deg, #e96c15 0%, #ff8a3c 100%);
  transition: min-height 0.25s ease;
}
/* Subtitle folds to zero height (not just opacity) so it gives its space
   back to the catalogue rather than leaving a gap. */
.header-sub {
  max-height: 1.2rem;
  overflow: hidden;
  opacity: 0.75;
  transition: max-height 0.25s ease, opacity 0.2s ease;
}
.preview-shell.is-scrolled .header-sub {
  max-height: 0;
  opacity: 0;
}

/*
  Sticky region anchors just below the brand header. CSS handles the
  vertical stacking via top: var(--header-h) so no JS measurement of
  the header height is needed across reloads. The top transition matches
  the header's collapse so the pill bar rides up with it instead of snapping.
*/
.sticky-bar {
  position: sticky;
  top: var(--header-h, 4.75rem);
  z-index: 2;
  box-shadow: 0 4px 12px -8px rgba(0, 0, 0, 0.15);
  transition: top 0.25s ease;
}

/* Search input gets a slightly bigger pill silhouette so it feels
   like the primary input on the page. */
.search-pill .input-group-text,
.search-pill .form-control {
  border-radius: 999px;
}
.search-pill .input-group-text {
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
}
.search-pill .form-control {
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
}
.search-pill .form-control:focus {
  box-shadow: none;
  border-color: #ced4da;
}
.search-pill .btn-outline-secondary {
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
}

.hide-scrollbar::-webkit-scrollbar { display: none; }
.hide-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }

/*
  Compact category pills (shared design with service/TabMenu). A thin
  chip that fills with the theme colour when active — lighter and smaller
  than the old Bootstrap outline buttons. Using a custom button also
  sidesteps the sticky touch :hover/:focus fill Bootstrap applied, which
  used to leave a tapped pill looking active.
*/
.cat-pill {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.3rem 0.7rem;
  font-size: 0.8rem;
  font-weight: 600;
  line-height: 1.4;
  white-space: nowrap;
  color: #6c757d;
  background: #fff;
  border: 1px solid #e7e7e7;
  border-radius: 999px;
  cursor: pointer;
  transition: color 0.18s ease, background-color 0.18s ease,
              border-color 0.18s ease, box-shadow 0.18s ease;
}
.cat-pill:focus,
.cat-pill:focus-visible {
  outline: none;
  box-shadow: none;
}
.cat-pill.active {
  color: #fff;
  background: #e96c15;
  border-color: #e96c15;
  box-shadow: 0 3px 10px rgba(233, 108, 21, 0.3);
}
.cat-pill-count {
  font-size: 0.68rem;
  font-weight: 700;
  min-width: 1.15rem;
  padding: 0 0.3rem;
  text-align: center;
  border-radius: 999px;
  background: rgba(0, 0, 0, 0.06);
  color: inherit;
}
.cat-pill.active .cat-pill-count {
  background: rgba(255, 255, 255, 0.25);
}

/* Section header: a thin theme accent bar + small heading + muted count.
   Lighter than the old h5 + "X items" badge, and shared with TabMenu. */
.section-accent {
  display: inline-block;
  width: 3px;
  height: 1rem;
  border-radius: 999px;
  background: linear-gradient(180deg, #e96c15 0%, #ff8a3c 100%);
}
.section-title {
  font-size: 1rem;
  letter-spacing: 0.1px;
}
.section-count {
  font-size: 0.72rem;
  font-weight: 700;
  color: #adb5bd;
}

/*
  Match the scroll-margin to the sticky bar's total height so an
  anchor jump (keyboard nav, deep-link, etc.) lands cleanly below the
  sticky region instead of being hidden behind it.
*/
.menu-section {
  scroll-margin-top: 11rem;
}

/* --- Product card (poster style) --------------------------------------
   The card is now a single full-bleed image with all metadata layered
   over a bottom gradient. Reads like a delivery-app hero tile or a
   magazine ad — meant to be browsed visually rather than scanned as
   a table of names.
*/
.product-card {
  display: block;
  border-radius: 18px;
  overflow: hidden;
  cursor: pointer;
  position: relative;
  background: #1d1d1d;            /* fallback shows through if image fails */
  aspect-ratio: 1 / 1;            /* square tile */
  min-height: 150px;
  transition: transform 0.25s ease, box-shadow 0.25s ease;
}
.product-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 16px 30px rgba(0, 0, 0, 0.18) !important;
}
.product-image {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.5s ease;
}
.product-card:hover .product-image {
  transform: scale(1.07);
}
.product-placeholder {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #e96c15 0%, #ff8a3c 100%);
  color: rgba(255, 255, 255, 0.7);
  font-size: 3rem;
}

/* Out-of-stock: desaturate the image and drop a centred "Out of Stock"
   badge so the item still shows on the branch menu but reads as unavailable. */
.product-card.is-soldout .product-image {
  filter: grayscale(1) brightness(0.7);
}
.product-card.is-soldout:hover .product-image {
  transform: none;
}
.soldout-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 3;
}
.soldout-badge {
  display: inline-flex;
  align-items: center;
  background: rgba(33, 37, 41, 0.85);
  color: #fff;
  font-weight: 700;
  font-size: 0.72rem;
  padding: 0.3rem 0.7rem;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.35);
  letter-spacing: 0.2px;
}

/* Two-stop gradient: transparent at the top, fading to dark at the
   bottom so the name/price overlay stays readable on any image. */
.product-gradient {
  position: absolute;
  inset: 0;
  background: linear-gradient(
    180deg,
    rgba(0, 0, 0, 0) 35%,
    rgba(0, 0, 0, 0.55) 70%,
    rgba(0, 0, 0, 0.85) 100%
  );
}

/*
  Top-left badge stack (matches service/TabMenu so the two pages feel
  related). Price pill is the primary mark; star sits inside it when
  the card is in the Recommended bucket. Options pill below.
*/
.overlay-badges {
  position: absolute;
  top: 0.55rem;
  left: 0.55rem;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.3rem;
  z-index: 2;
  max-width: calc(100% - 1.1rem);
}
.overlay-price {
  display: inline-flex;
  align-items: center;
  background: #e96c15;
  color: #fff;
  font-weight: 800;
  padding: 0.25rem 0.65rem;
  border-radius: 999px;
  font-size: 0.78rem;
  box-shadow: 0 3px 8px rgba(0, 0, 0, 0.25);
  white-space: nowrap;
  max-width: 100%;
}
.overlay-options {
  display: inline-flex;
  align-items: center;
  background: rgba(255, 255, 255, 0.95);
  color: #d46010;
  border: 1px solid rgba(233, 108, 21, 0.25);
  font-weight: 600;
  padding: 0.2rem 0.55rem;
  border-radius: 999px;
  font-size: 0.68rem;
  box-shadow: 0 3px 8px rgba(0, 0, 0, 0.2);
  backdrop-filter: blur(4px);
  white-space: nowrap;
}

.product-overlay {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  padding: 0.7rem 0.85rem 0.85rem;
  z-index: 2;
  text-shadow: 0 1px 4px rgba(0, 0, 0, 0.5);
}
.overlay-name {
  font-size: 0.88rem;
  /* 1.5 (not 1.25) so Thai upper tone marks (ก๋, เล้ง) and lower vowels
     (ตุ๋น) aren't clipped by the line-clamp's overflow:hidden box. */
  line-height: 1.8;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  overflow-wrap: anywhere;
}
/* Extra-narrow phones: trim one notch so 2 columns stay tidy. */
@media (max-width: 380px) {
  .overlay-name { font-size: 0.8rem; }
  .overlay-price {
    font-size: 0.7rem;
    padding: 0.22rem 0.55rem;
  }
  .overlay-options { font-size: 0.62rem; }
}

.skeleton-shimmer {
  background: linear-gradient(90deg, #eceff2 0%, #f5f7f9 50%, #eceff2 100%);
  background-size: 200% 100%;
  animation: pos-skeleton-shimmer 1.25s ease-in-out infinite;
  display: block;
}
.skeleton-poster {
  border-radius: 18px;
  aspect-ratio: 1 / 1;
  min-height: 150px;
}
@keyframes pos-skeleton-shimmer {
  0%   { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

.fade-swap-enter-active,
.fade-swap-leave-active {
  transition: opacity 0.15s ease;
}
.fade-swap-enter-from,
.fade-swap-leave-to {
  opacity: 0;
}

/* Per-section entry stagger — settles the page into place after the
   skeleton clears without a slow ripple. Matches service/TabMenu. */
.section-enter {
  animation: section-enter-in 0.25s ease-out both;
  animation-delay: var(--enter-delay, 0ms);
}
@keyframes section-enter-in {
  from { opacity: 0; transform: translateY(8px); }
  to   { opacity: 1; transform: translateY(0); }
}
</style>
